import { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle, Clock } from 'lucide-react';
import { marketApi } from '../../services/api';
import { useProductStore } from '../../store/productStore';

interface Quote {
  symbol: string;
  name: string;
  price: number;
  change: number | null;
  percent_change: number | null;
  timestamp: string | null;
}

interface MarketData {
  quotes: Quote[];
  stale?: boolean;
  market_open?: boolean;
  error?: string;
}

// Poll every 5 minutes during market hours; stop outside market hours
const POLL_INTERVAL_MS = 300_000;

const TICKER_ICONS: Record<string, string> = {
  CORN: '\u{1F33D}',
  SOYB: '\u{1FAD8}',
  WEAT: '\u{1F33E}',
  LC: '\u{1F404}',
  FC: '\u{1F402}',
};

const GRAIN_SYMBOLS = ['CORN', 'SOYB', 'WEAT'];
const BEEF_SYMBOLS = ['LC', 'FC'];

export default function PriceTicker() {
  const { activeProduct } = useProductStore();
  const [data, setData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const disabledRef = useRef(false);

  const fetchPrices = async () => {
    if (disabledRef.current) return;
    try {
      const res = await marketApi.getPrices();
      setData(res.data);

      // If market is closed, stop polling - we have closing prices
      if (res.data.market_open === false) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 503) {
        disabledRef.current = true;
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
    intervalRef.current = setInterval(fetchPrices, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (loading || !data || data.quotes.length === 0) {
    return null;
  }

  // Filter quotes based on active product
  const activeSymbols = activeProduct === 'beef' ? BEEF_SYMBOLS : GRAIN_SYMBOLS;
  const filteredQuotes = data.quotes.filter(q => activeSymbols.includes(q.symbol));

  if (filteredQuotes.length === 0) {
    return null;
  }

  // Duplicate quotes for seamless loop
  const tickerItems = [...filteredQuotes, ...filteredQuotes, ...filteredQuotes];

  return (
    <div
      className="relative bg-gray-900 text-white text-sm overflow-hidden shadow-md"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Static label */}
      <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center px-3 bg-gray-900">
        <span className="text-gray-400 font-bold uppercase tracking-wider text-[11px]">
          {activeProduct === 'beef' ? 'Cattle' : 'Grain'}
        </span>
      </div>

      {/* Scrolling track */}
      <div className="pl-16 py-2">
        <div
          className={`flex items-center gap-6 ticker-scroll ${paused ? 'ticker-paused' : ''}`}
        >
          {tickerItems.map((q, i) => (
            <QuotePill key={`${q.symbol}-${i}`} quote={q} />
          ))}
          {data.market_open === false && (
            <span className="flex items-center gap-1 text-gray-400 flex-shrink-0" title="Market closed - showing closing prices">
              <Clock className="w-3 h-3" />
              <span className="text-[10px]">closed</span>
            </span>
          )}
          {data.stale && (
            <span className="flex items-center gap-1 text-amber-400 flex-shrink-0" title="Using cached data">
              <AlertCircle className="w-3 h-3" />
              <span className="text-[10px]">delayed</span>
            </span>
          )}
        </div>
      </div>

      {/* Fade edges */}
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-900 to-transparent z-10 pointer-events-none" />

      {/* V2 preview marker */}
      <div className="absolute right-0 top-0 bottom-0 z-20 flex items-center bg-gray-900 pl-4 pr-3">
        <span className="inline-flex items-center rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-300">
          V2 preview
        </span>
      </div>
    </div>
  );
}

function QuotePill({ quote }: { quote: Quote }) {
  const { symbol, name, price, change, percent_change } = quote;
  const isUp = change !== null && change > 0;
  const isDown = change !== null && change < 0;
  const icon = TICKER_ICONS[symbol] || '';

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className="text-gray-200 font-semibold whitespace-nowrap">
        {icon} {name}
      </span>
      <span className="font-bold tabular-nums">${price.toFixed(2)}</span>
      {change !== null && percent_change !== null && (
        <span
          className={`flex items-center gap-0.5 font-semibold ${
            isUp ? 'text-emerald-400' : isDown ? 'text-red-400' : 'text-gray-400'
          }`}
        >
          {isUp ? (
            <TrendingUp className="w-3 h-3" />
          ) : isDown ? (
            <TrendingDown className="w-3 h-3" />
          ) : (
            <Minus className="w-3 h-3" />
          )}
          <span className="tabular-nums whitespace-nowrap">
            {isUp ? '+' : ''}{percent_change.toFixed(2)}%
          </span>
        </span>
      )}
      <span className="text-gray-600 mx-1">|</span>
    </div>
  );
}
